import React, {
  ForwardedRef,
  forwardRef,
  KeyboardEvent,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.min.css';
import { css, Global } from '@emotion/react';
import ReactInputMask, { Props } from 'react-input-mask';
import { format, getYear, isValid, set } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import Input, { InputProps } from '../../../tradevan-ui/Input';
import { dateStrParse, dateMaskTransfer, getCharStr } from '../consts';
import { ErrMsgEnum } from '../../../tradevan-ui/config';
import DateEnum from '../enum';
import { VerifyProps } from '../../../tradevan-ui/type';
import { isNull } from '../../../tradevan-ui/consts';
import YearMonthHeader from '../component/YearMonthHeader';

export interface DatePickerProps
  extends Omit<
      ReactDatePickerProps,
      'name' | 'value' | 'selected' | 'onChange'
    >,
    VerifyProps<Date | null> {
  /** 欄位名稱(改為 required) */
  name: string;
  /** 改寫 value 傳入 Date, 並移除 selected */
  value: Date | null;
  /** 改寫 onChange 事件，回傳 { name, value } */
  onChange?({ name, value }: { name: string; value: Date | null }): void;
  /** 日期顯示格式： dateFormat 改為指定格式 DateEnum */
  dateFormat?: DateEnum | string;
  /**
   * #### react-input-mask 的 mask 屬性，需搭配 dateFormat 使用。
   *
   * Mask string. Format characters are:
   * * `9`: `0-9`
   * * `a`: `A-Z, a-z`
   * * `\*`: `A-Z, a-z, 0-9`
   *
   * Any character can be escaped with backslash, which usually will appear as double backslash in JS strings.
   * For example, German phone mask with unremoveable prefix +49 will look like `mask="+4\\9 99 999 99"` or `mask={"+4\\\\9 99 999 99"}`
   */
  mask?: Props['mask'];
  /**
   * #### [`react-input-mask`](https://github.com/sanniassin/react-input-mask#mask)  的 props 屬性。
   */
  inputMaskProps?: Omit<Props, 'mask'>;
  /**
   * value 為無效 Date 時，顯示的錯誤文字，ex: 請輸入正確的時間
   * @default ErrMsgEnum.date ('請輸入正確的日期。')
   */
  invalidMsg?: string;
  /** 日期 string 轉 Date 函式，預設為 dateStrParse，但民國年需例外處理 */
  parseFunc?(value: string): Date | null;
  /** 日期 Date 轉 string 函式，預設為 format，但民國年需例外處理 */
  formatFunc?(date: Date): string;
  /** 最小日期 (類型指定為 Date) */
  minDate?: Date;
  /** 最大日期 (類型指定為 Date) */
  maxDate?: Date;
  /** 是否Disable */
  isDisabled?: boolean;
}

const DatePicker = forwardRef(
  (props: DatePickerProps, ref: ForwardedRef<HTMLInputElement>) => {
    const {
      name,
      value,
      dateFormat = DateEnum['yyyy/MM/dd'],
      mask = dateMaskTransfer(dateFormat),
      inputMaskProps,
      onChange,
      error,
      helperText,
      invalidMsg = ErrMsgEnum.date,
      locale = zhTW,
      calendarStartDay = 0,
      parseFunc = dateStrParse(dateFormat),
      formatFunc = (d) => format(d, dateFormat),
      onChangeRaw,
      onSelect,
      onBlur,
      minDate: minDateTemp = new Date(1990, 0, 1),
      maxDate: maxDateTemp = new Date(getYear(new Date()) + 50, 0, 1),
      renderCustomHeader,
      verifyBeforeChange,
      verifiers = [],
      onMsgChange,
      required,
      showTimeSelect,
      isDisabled,
      ...other
    } = props;

    const minDate = set(minDateTemp, {
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    });

    const maxDate = set(maxDateTemp, {
      hours: 23,
      minutes: 59,
      seconds: 59,
      milliseconds: 59,
    });

    const [message, setMessage] = useState('');
    const [firstVerify, setFirstVerify] = useState(verifyBeforeChange);

    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      setFirstVerify(verifyBeforeChange);
    }, [verifyBeforeChange]);

    // html required 無效，因為 input readonly
    if (required) {
      verifiers.push({
        func: (d) => !d,
        text: ErrMsgEnum.required,
      });
    }

    // value 為無效 Date 時，改為 null 並顯示錯誤訊息
    // useEffect(() => {
    //   const isValidDate = isValid(value);
    //   if (isValidDate || isNull(value)) {
    //     setMessage('');
    //     // setLocalValue(value);
    //     setLocalValue(null);
    //   } else {
    //     setLocalValue(value);
    //     // if (onChange) onChange({ name, value: null });
    //     setMessage(invalidMsg);
    //   }
    // }, [value]);

    useEffect(() => {
      // 始設畫面預設不 verify , 除非 verifyBeforeChange = true
      if (!firstVerify) {
        setMessage('');
        return;
      }

      // 驗使用者設定之 verifier, 不驗空值(給 HTML required 驗)
      if (verifiers.length > 0) {
        const veriObj = verifiers.find((obj) => obj.func(value));
        if (veriObj) {
          // if (onChange) onChange({ name, value: null });
          setMessage(veriObj.text);
          return;
        }
      }

      // value 為無效 Date 時，改為 null 並顯示錯誤訊息
      const isValidDate = isValid(value);
      if (!(isValidDate || isNull(value))) {
        // if (onChange) onChange({ name, value: null });
        setMessage(invalidMsg);
      }

      // 先前檢驗都 pass
      // setLocalValue(value);
      setMessage('');
    }, [value, firstVerify]);

    /** 檢核日期字串，並重設錯誤訊息 */
    const verifyInputStr = (str: string) => {
      const date = parseFunc(str);
      const isValidDate = isValid(date);

      // 空值時
      if (!str || !date) {
        if (required) setMessage(ErrMsgEnum.required);
        else setMessage('');
      }
      // 無效時
      else if (!isValidDate) setMessage(invalidMsg);
      // 小於 minDate
      else if (date < minDate)
        setMessage(`${ErrMsgEnum.minDate + formatFunc(minDate)}。`);
      // 大於 minDate
      else if (date > maxDate)
        setMessage(`${ErrMsgEnum.maxDate + formatFunc(maxDate)}。`);
      // 通過檢核
      else setMessage('');
    };

    useEffect(() => {
      if (onMsgChange) onMsgChange({ name, value: message });
    }, [message]);

    const selected = isValid(value) ? value : null;
    const localError = message.length > 0 || error;
    const localHelperText = message || helperText;

    const handleOnChangeRaw = (
      e: React.FocusEvent<HTMLInputElement, Element>,
    ) => {
      // e.stopPropagation();
      if (onChangeRaw) onChangeRaw(e);

      if (onChange) {
        const v = e.target.value || '';
        const vDate = parseFunc(v);
        if (vDate && isValid(vDate) && vDate >= minDate && vDate <= maxDate)
          onChange({ name, value: vDate });
        // 清空
        else if (
          getCharStr(dateFormat, v, [
            'Y',
            'y',
            'M',
            'm',
            'D',
            'd',
            'H',
            'h',
            'S',
            's',
          ]).replace(/_/g, '').length === 0
        )
          onChange({ name, value: null });
      }

      // 輸入無效日期時，顯示錯誤訊息，但不觸發 onChange
      // 檢驗 Input 是否為有效日期 string (change 才驗, focus/blur 不驗)
      if (e.type !== 'change') return;
      verifyInputStr(e.target.value);
    };

    const handleOnChange = (date: Date | null) => {
      if (showTimeSelect && onChange) onChange({ name, value: date });
    };

    const handleOnSelect = (
      date: Date,
      e?: React.SyntheticEvent<unknown, Event>,
    ) => {
      e?.stopPropagation();
      // #region
      /** input 按 Enter 打開 Picker，再按 Enter 時會進入此事件，
       * 但此時於 renderCustomHeader 時，會觸發第一顆按鈕的 onClick，
       * 造成月份遞減，故設定條件排除 (不更新 value)
       * 1. onSelect 事件是由 Enter 觸發，且 e.target 是 Input
       * 2. value: null 時不會有上述狀況且能選到今日，故設定例外
       */
      const keyEvent = e as unknown as SyntheticEvent<
        HTMLInputElement,
        KeyboardEvent
      >;
      if (
        keyEvent.nativeEvent.key === 'Enter' &&
        inputRef.current === e?.target &&
        value
      )
        return;
      // #endregion

      if (onSelect) onSelect(date, e);
      if (onChange) onChange({ name, value: date });
    };

    const handleOnBlue = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      if (onBlur) onBlur(e);
      verifyInputStr(e.target.value);
    };

    return (
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Global
          styles={css`
            // 三角箭頭位置有 bug
            .react-datepicker__triangle {
              left: -50px !important;
            }
            // 年月下拉選單的 icon 沒有空間
            .react-datepicker__month-dropdown-container--scroll {
              margin-right: 20px;
            }
            .react-datepicker-popper {
              z-index: 3;
            }
          `}
        />
        <ReactDatePicker
          selected={selected}
          customInput={
            <ReactInputMask {...{ ...inputMaskProps, mask, name }}>
              {
                // 不知為何報錯先跳過
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                (inputProps: Props & InputProps) => (
                  <Input
                    type="tel"
                    // required 放這才有效果
                    {...{ ...inputProps, required }}
                    ref={(ele) => {
                      if (typeof ref === 'function') ref(ele);
                      inputRef.current = ele;
                    }}
                    verifiers={[
                      {
                        func: (v) => !v && !isValid(parseFunc(v)),
                        text: invalidMsg,
                      },
                    ]}
                    error={localError}
                    helperText={localHelperText}
                    /** 加上 icon */
                    inputGroupProps={{ className: 'i-date' }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isDisabled}
                  />
                )
              }
            </ReactInputMask>
          }
          renderCustomHeader={(props2) => {
            const propsTemp = {
              minDate,
              maxDate,
              error: localError,
              helperText: localHelperText,
              ...props2,
              // 民國年會有不預期的 date 改傳 selected
              date: selected || new Date(),
            };
            return renderCustomHeader ? (
              renderCustomHeader(propsTemp)
            ) : (
              <YearMonthHeader {...propsTemp} />
            );
          }}
          // onChange 有日期轉換問題, 改使用 onChangeRaw + onSelect 處理
          onChangeRaw={handleOnChangeRaw}
          onChange={handleOnChange}
          // date 有效時才會觸發，清空時因為無效未觸發
          onSelect={(v, e) => {
            if (onChange) {
              handleOnSelect(v, e);
            }
          }}
          onBlur={handleOnBlue}
          {...{
            name,
            locale,
            dateFormat,
            minDate,
            maxDate,
            calendarStartDay,
            showTimeSelect,
            ...other,
          }}
        />
      </span>
    );
  },
);

DatePicker.displayName = 'DatePicker';
export default DatePicker;
